import BigNumber from 'bignumber.js';
import moment from 'moment';
import { DataAggregationFunction, LiteralType, SortDirection, ValueDataType } from 'src/app/services/api.services';
import { isNullOrUndefined } from '../../shared/utils';
import { createDateTimeValue, createDateValue, createTimeValue, DateTimeValue, DateValue, TimeValue } from '../dataQuery/expr';
import { DataAggregation } from './DataAggregation';

export interface DataTypeOptions {
  readonly name: string;
  readonly precedence: number;
  readonly icon: string;
  readonly title: string;
  readonly description: string;
  readonly singularValueText: string;
  readonly pluralValueText: string;
  readonly defaultAggregation?: DataAggregationFunction;
  readonly literalType?: LiteralType;
  readonly defaultSortDirection?: SortDirection;

  /** Simple parsing function that recognised common versions of the data type. */
  readonly tryParse?: (str: string) => any;
}

export class DataType {
  constructor(
    public readonly value: ValueDataType,
    private readonly options: DataTypeOptions) { }

  get name() { return this.options.name; }
  get precedence() { return this.options.precedence; }

  get icon() { return this.options.icon; }
  get title() { return this.options.title; }
  get description() { return this.options.description; }

  get isBoolean() { return this.value == ValueDataType.Boolean; }

  get isDecimal() { return this.value == ValueDataType.Decimal; }
  get isInteger() { return this.value == ValueDataType.Integer; }
  get isNumber() { return this.value == ValueDataType.Number; }

  get isNumeric() {
    return this.isDecimal || this.isInteger || this.isNumber;
  }

  get isDateOnly() { return this.value == ValueDataType.DateOnly; }
  get isTimeOnly() { return this.value == ValueDataType.TimeOnly; }
  get isDateTime() { return this.value == ValueDataType.DateTime; }

  get isDateOrDateTime() { return this.isDateOnly || this.isDateTime; }
  get isTimeOrDateTime() { return this.isTimeOnly || this.isDateTime; }

  get isText() { return this.value == ValueDataType.Text; }

  get isList() { return this.value == ValueDataType.List; }
  get isMap() { return this.value == ValueDataType.Map; }

  get singularValueText() { return this.options.singularValueText; }
  get pluralValueText() { return this.options.pluralValueText; }

  get defaultAggregation() { return DataAggregation.from(this.options.defaultAggregation); }
  get defaultSortDirection() { return this.options.defaultSortDirection; }

  valueOf() {
    return this.value;
  }

  toString() {
    return this.value;
  }

  isAssignableTo(assignee: DataType, downcastNotAllowed?: boolean) {
    let conversion = DataType.classifyConversion(assignee, this);

    return (conversion <= DataType.ConversionClassification.None)
      || (conversion == DataType.ConversionClassification.Downcast && !downcastNotAllowed);
  }

  isAssignableFrom(other: DataType, downcastNotAllowed?: boolean) {
    return other.isAssignableTo(this, downcastNotAllowed);
  }

  isDirectlyAssignableTo(other: DataType) {
    if (this.isNumeric && other.isNumeric)
      return true;

    if (this.isDateOrDateTime && other.isDateOrDateTime)
      return true;

    if (this.isTimeOnly && other.isTimeOnly)
      return true;

    return this == other;
  }

  canAggregateWith(aggregation: DataAggregation) {
    if (!aggregation)
      return true;

    return aggregation.canUseFor(this);
  }

  get literalType() {
    return this.options.literalType;
  }

  get tryParse() {
    return this.options.tryParse;
  }
}

export namespace DataType {
  export const Boolean = new DataType(ValueDataType.Boolean, {
    name: 'bool',
    precedence: 1000,
    icon: `fas fa-adjust`,
    title: `Boolean`,
    description: `Data type that can only take value of either TRUE or FALSE.`,
    singularValueText: `a boolean`,
    pluralValueText: `booleans`,
    defaultAggregation: DataAggregationFunction.CountNonNull,
    literalType: LiteralType.Boolean,
    defaultSortDirection: SortDirection.Ascending,
    tryParse: str => {
      if (isNullOrUndefined(str))
        return null;

      str = str.toLowerCase();

      if (str == 'true')
        return true;

      if (str == 'false')
        return false;

      return undefined;
    }
  });

  export const Integer = new DataType(ValueDataType.Integer, {
    name: 'int',
    precedence: 1100,
    icon: `far fa-plus`,
    title: `Integer`,
    description: `Numeric data type without fraction.`,
    singularValueText: `an integer`,
    pluralValueText: `integers`,
    defaultAggregation: DataAggregationFunction.Sum,
    literalType: LiteralType.Integer,
    defaultSortDirection: SortDirection.Ascending,
    tryParse: str => {
      if (isNullOrUndefined(str))
        return null;

      let n = window.Number(str);
      if (isNaN(n))
        return undefined;

      return n;
    }
  });

  export const Number = new DataType(ValueDataType.Number, {
    name: 'number',
    precedence: 1200,
    icon: `far fa-plus`,
    title: `Number`,
    description: `Numeric data type with limited precision.`,
    singularValueText: `a number`,
    pluralValueText: `numbers`,
    defaultAggregation: DataAggregationFunction.Sum,
    literalType: LiteralType.Number,
    defaultSortDirection: SortDirection.Ascending,
    tryParse: str => {
      if (isNullOrUndefined(str))
        return null;

      let n = window.Number(str);
      if (isNaN(n))
        return undefined;

      return n;
    }
  });

  export const Decimal = new DataType(ValueDataType.Decimal, {
    name: 'decimal',
    precedence: 1300,
    icon: `far fa-plus`,
    title: `Decimal`,
    description: `Numeric data type with arbitrary precision.`,
    singularValueText: `a number`,
    pluralValueText: `numbers`,
    defaultAggregation: DataAggregationFunction.Sum,
    literalType: LiteralType.Decimal,
    defaultSortDirection: SortDirection.Ascending,
    tryParse: str => {
      if (isNullOrUndefined(str))
        return null;

      let n = window.Number(str);
      if (isNaN(n))
        return undefined;

      return n;
    }
  });


  export const DateOnly = new DataType(ValueDataType.DateOnly, {
    name: 'dateOnly',
    precedence: 2000,
    icon: `far fa-calendar-alt`,
    title: `Date`,
    description: `Data type representing date only.`,
    singularValueText: `a date value`,
    pluralValueText: `date values`,
    defaultAggregation: DataAggregationFunction.CountNonNull,
    literalType: LiteralType.Date,
    defaultSortDirection: SortDirection.Descending,
    tryParse: str => str ? createDateValue(str) : null
  });

  export const TimeOnly = new DataType(ValueDataType.TimeOnly, {
    name: 'timeOnly',
    precedence: 2100,
    icon: `far fa-clock`,
    title: `Time`,
    description: `Data type representing time of the day.`,
    singularValueText: `a time value`,
    pluralValueText: `time values`,
    defaultAggregation: DataAggregationFunction.CountNonNull,
    literalType: LiteralType.Time,
    defaultSortDirection: SortDirection.Ascending,
    tryParse: str => str ? createTimeValue(str) : null
  });

  export const DateTime = new DataType(ValueDataType.DateTime, {
    name: 'dateTime',
    precedence: 2200,
    icon: `far fa-calendar-alt`,
    title: `Date & Time`,
    description: `Data type representing both date and time.`,
    singularValueText: `a date & time value`,
    pluralValueText: `date & time values`,
    defaultAggregation: DataAggregationFunction.CountNonNull,
    literalType: LiteralType.DateTime,
    defaultSortDirection: SortDirection.Descending,
    tryParse: str => str ? createDateTimeValue(str, 'utc') : null
  });


  export const Text = new DataType(ValueDataType.Text, {
    name: 'text',
    precedence: 8000,
    icon: `fas fa-text`,
    title: `Text`,
    description: `Data type representing text.`,
    singularValueText: `text value`,
    pluralValueText: `text values`,
    defaultAggregation: DataAggregationFunction.CountNonNull,
    literalType: LiteralType.String,
    defaultSortDirection: SortDirection.Ascending,
    tryParse: str => str
  });


  export const List = new DataType(ValueDataType.List, {
    name: 'list',
    precedence: 0,
    icon: `far fa-cubes`,
    title: `List`,
    description: ``,
    singularValueText: `list`,
    pluralValueText: `lists`,
    defaultAggregation: DataAggregationFunction.CountNonNull,
    literalType: undefined,
  });

  export const Map = new DataType(ValueDataType.Map, {
    name: 'map',
    precedence: 0,
    icon: `far fa-cubes`,
    title: `Map`,
    description: ``,
    singularValueText: `map`,
    pluralValueText: `maps`,
    defaultAggregation: DataAggregationFunction.CountNonNull,
    literalType: undefined,
  });


  export const Unknown = new DataType(ValueDataType.Map, {
    name: 'unknown',
    precedence: 0,
    icon: `fas fa-asterisk`,
    title: `Unknown`,
    description: ``,
    singularValueText: `unknown`,
    pluralValueText: `unknown`,
    defaultAggregation: DataAggregationFunction.CountNonNull,
    literalType: undefined,
  });

  export const DataTypeList = Object.values(ValueDataType)
    .map(d => {
      if(d != ValueDataType.Unknown)
        return from(d);

      return DataType.Unknown;
    });

  export function from(value: ValueDataType) {
    switch (value) {
      case ValueDataType.Boolean: return Boolean;

      case ValueDataType.Integer: return Integer;
      case ValueDataType.Number: return Number;
      case ValueDataType.Decimal: return Decimal;

      case ValueDataType.DateOnly: return DateOnly;
      case ValueDataType.TimeOnly: return TimeOnly;
      case ValueDataType.DateTime: return DateTime;

      case ValueDataType.Text: return Text;

      case ValueDataType.List: return List;
      case ValueDataType.Map: return Map;

      case ValueDataType.Unknown: return Unknown;
    }

    throw new Error(`Invalid ValueDataType`, { cause: value });
  }

  export enum ConversionClassification {
    Upcast,
    None,
    Downcast,
    Invalid,
  }

  export function classifyConversion(target: DataType, value: DataType): ConversionClassification {
    if (target === value)
      return DataType.ConversionClassification.None;

    // unknown := any (upcast)
    if (target === DataType.Unknown)
      return DataType.ConversionClassification.Upcast;

    // any := unknown (downcast)
    if (value === DataType.Unknown)
      return DataType.ConversionClassification.Downcast;

    if ((value.isList || value.isMap) || (target.isList || target.isMap)) {
      // conversion from/to list/map is not allowed
      return DataType.ConversionClassification.Invalid;
    }

    if (target.isBoolean) {
      // boolean := numeric (downcast)
      if (value.isNumeric)
        return DataType.ConversionClassification.Downcast;

      // boolean := text (downcast)
      if (value.isText)
        return DataType.ConversionClassification.Downcast;
    }
    else if (target.isInteger) {
      // integer := numeric (downcast)
      if (value.isNumeric)
        return DataType.ConversionClassification.Downcast;

      // integer := boolean (upcast)
      if (value.isBoolean)
        return DataType.ConversionClassification.Upcast;

      // integer := text (downcast)
      if (value.isText)
        return DataType.ConversionClassification.Downcast;
    }
    else if (target.isNumber || target.isDecimal) {
      // numeric := numeric
      if (value.isNumeric)
        return DataType.ConversionClassification.None;

      // numeric := boolean (upcast)
      if (value.isBoolean)
        return DataType.ConversionClassification.Upcast;

      // numeric := text (downcast)
      if (value.isText)
        return DataType.ConversionClassification.Downcast;
    }
    else if (target.isDateOnly) {
      // date := dateTime (downcast)
      if (value.isDateTime)
        return DataType.ConversionClassification.Downcast;

      // date := text (downcast)
      if (value.isText)
        return DataType.ConversionClassification.Downcast;
    }
    else if (target.isTimeOnly) {
      // time := text (downcast)
      if (value.isText)
        return DataType.ConversionClassification.Downcast;
    }
    else if (target.isDateTime) {
      // dateTime := date (upcast)
      if (value.isDateTime)
        return DataType.ConversionClassification.Downcast;

      // dateTime := text (downcast)
      if (value.isText)
        return DataType.ConversionClassification.Downcast;
    }
    else if (target.isText) {
      // text := any (upcast)
      return DataType.ConversionClassification.Upcast;
    }

    return DataType.ConversionClassification.Invalid;
  }

  export function getLeftSideResult(args: DataType[], downcastNotAllowed?: boolean): DataType | null {
    let l = args[0];

    let maxConversionType = downcastNotAllowed ? ConversionClassification.Downcast : ConversionClassification.Invalid;

    for (let i = 1; i < args.length; ++i) {
      let r = args[i];
      if (l === r)
        continue;

      let rightToLeft = classifyConversion(l, r);
      let leftToRight = classifyConversion(r, l);

      if (rightToLeft < maxConversionType) {
        if (leftToRight < maxConversionType) {
          l = (leftToRight < rightToLeft) ? r : l;
        }

        continue;
      }

      if (leftToRight < maxConversionType) {
        l = r;
        continue;
      }

      return null;
    }

    return l;
  }
  

  type SortFunc = (a: any, b: any) => number;

  export function getSortFunction(dataType: DataType, options?: { convertText: boolean }): SortFunc {
    let sort: SortFunc = (a, b) => a - b;
    let transform = (f: ((x: SortFunc) => SortFunc)) => sort = f(sort);

    if (dataType.isBoolean) {
      function order(x: any) {
        if (typeof x === 'boolean')
          return x ? 1 : -1;

        if (typeof x === 'number')
          return x != 0;
          
        if (typeof x === 'bigint')
          return x != BigInt(0);

        if (BigNumber.isBigNumber(x))
          return !x.isZero();

        return window.Number.MAX_VALUE;
      }

      transform(x => ((a, b) => {
        return x(order(a), order(b));
      }));
    }
    else if (dataType.isNumeric) {
      function order(x: any) {
        if (typeof x === 'number')
          return x;

        if (typeof x === 'bigint')
          return window.Number(x);

        if (BigNumber.isBigNumber(x))
          return x.toNumber();

        return window.Number.MAX_VALUE;
      }

      transform(x => ((a, b) => {
        return x(order(a), order(b));
      }));
    }
    else if (dataType.isDateOnly) {
      function order(x: any) {
        if (x instanceof Date)
          return moment.utc(x).startOf('day').unix();

        if (x instanceof DateValue)
          return x.toMoment().unix();
          
        if (x instanceof DateTimeValue)
          return x.date.toMoment().unix();

        return window.Number.MAX_VALUE;
      }

      transform(x => ((a, b) => {
        return x(order(a), order(b));
      }));
    }
    else if (dataType.isDateOrDateTime) {
      function order(x: any) {
        if (x instanceof Date)
          return moment.utc(x).unix();

        if (x instanceof DateValue)
          return x.toMoment().unix();
          
        if (x instanceof DateTimeValue)
          return x.toMoment().unix();

        return window.Number.MAX_VALUE;
      }

      transform(x => ((a, b) => {
        return x(order(a), order(b));
      }));
    }
    else if (dataType.isTimeOnly) {
      function order(x: any) {
        if (x instanceof Date || x instanceof DateValue)
          return 0;

        if (x instanceof DateTimeValue)
          return x.time.value;

        return window.Number.MAX_VALUE;
      }

      transform(x => ((a, b) => {
        return x(order(a), order(b));
      }));
    }
    else if (dataType.isText) {
      function stringify(x: any): string {
        if (typeof x === 'string')
          return x;

        if ('toString' in x)
          return x.toString();

        return '' + x;
      }

      transform(x => ((a, b) => {
        if (isNullOrUndefined(a) || isNaN(a)) {
          if (isNullOrUndefined(b) || isNaN(b)) {
            return 0;
          }

          return window.Number.MIN_VALUE;
        }
        else {
          if (isNullOrUndefined(b) || isNaN(b)) {
            return window.Number.MAX_VALUE;
          }
        }

        return stringify(a).localeCompare(stringify(b));
      }));
    }
    
    if (options?.convertText && dataType.tryParse) {
      transform(x => ((a, b) => {
        a = dataType.tryParse(a);
        b = dataType.tryParse(b);
        return x(a, b);
      }));
    }
    
    return sort;
  }
}
